import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardList, Send, X, Lock } from "lucide-react";

const POSITIONS = ["Apotheker/in", "PTA", "PKA", "Botenfahrer", "Reinigungskraft", "Student", "Praktikant", "Filialleiter"];

const empty = { title: "", description: "", options: [{ label: "" }, { label: "" }], allow_multi: false, allow_comment: true, anonymous: false, target_positions: [], closes_at: "" };

export default function SurveysPage() {
  const { user, isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [voteFor, setVoteFor] = useState(null);
  const [voteChoice, setVoteChoice] = useState(null);
  const [voteComment, setVoteComment] = useState("");

  const load = async () => setItems((await api.get("/surveys")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const opts = form.options.filter((o) => o.label.trim());
      if (opts.length < 2) { toast.error("Mindestens 2 Antwortoptionen"); return; }
      await api.post("/surveys", { ...form, options: opts, closes_at: form.closes_at || null });
      toast.success("Umfrage veröffentlicht");
      setDialog(false); setForm(empty); await load();
    } catch (err) { toast.error("Fehler"); }
  };

  const vote = async () => {
    try {
      await api.post(`/surveys/${voteFor.id}/vote`, { option_id: voteChoice, comment: voteComment });
      toast.success("Danke für deine Stimme");
      setVoteFor(null); setVoteChoice(null); setVoteComment("");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const closeSurvey = async (id) => { try { await api.post(`/surveys/${id}/close`); toast.success("Geschlossen"); await load(); } catch (_e) { toast.error("Fehler"); } };
  const approveSurvey = async (id) => { try { await api.post(`/surveys/${id}/approve`); toast.success("Umfrage freigegeben"); await load(); } catch (_e) { toast.error("Fehler"); } };
  const del = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/surveys/${id}`); await load(); };

  const togglePos = (p) => {
    const set = new Set(form.target_positions);
    set.has(p) ? set.delete(p) : set.add(p);
    setForm({ ...form, target_positions: Array.from(set) });
  };

  const myVote = (s) => s.responses?.find((r) => r.user_id === user.id);
  const voteCounts = (s) => {
    const counts = {};
    s.options.forEach((o) => { counts[o.id] = 0; });
    s.responses.forEach((r) => { if (r.option_id) counts[r.option_id] = (counts[r.option_id] || 0) + 1; });
    return counts;
  };
  const total = (s) => s.responses.filter((r) => r.option_id).length;

  return (
    <div className="space-y-6" data-testid="surveys-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Team</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Umfragen</h2>
        </div>
        <Button data-testid="survey-add-btn" onClick={() => setDialog(true)} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Umfrage erstellen</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((s) => {
          const counts = voteCounts(s);
          const tot = total(s);
          const mine = myVote(s);
          return (
            <Card key={s.id} data-testid={`survey-${s.id}`} className={`border-border/60 card-hover ${s.closed ? "opacity-70" : ""}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                    <h3 className="font-heading font-bold text-lg">{s.title}</h3>
                  </div>
                  <div className="flex gap-1">
                    {s.closed && <Badge variant="secondary"><Lock className="w-3 h-3 mr-1" />Geschlossen</Badge>}
                    {!s.approved && <Badge className="bg-amber-100 text-amber-800 border-amber-200">Wartet auf Freigabe</Badge>}
                    {s.anonymous && <Badge variant="outline">Anonym</Badge>}
                  </div>
                </div>
                {s.description && <p className="text-sm text-slate-500 mb-3 whitespace-pre-wrap">{s.description}</p>}
                {s.target_positions?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {s.target_positions.map((p) => <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>)}
                  </div>
                )}
                <div className="space-y-2 mb-3">
                  {s.options.map((o) => {
                    const c = counts[o.id] || 0;
                    const pct = tot > 0 ? (c / tot) * 100 : 0;
                    const isMine = mine?.option_id === o.id;
                    return (
                      <div key={o.id} className="relative">
                        <div className="flex items-center justify-between text-sm">
                          <span className={isMine ? "font-bold text-blue-700" : ""}>{o.label}</span>
                          <span className="text-slate-500 text-xs">{c} · {pct.toFixed(0)}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full mt-1 overflow-hidden">
                          <div className={`h-full rounded-full ${isMine ? "bg-blue-600" : "bg-slate-400"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-border/60">
                  <div className="text-xs text-slate-500">{tot} Antworten</div>
                  <div className="flex gap-2">
                    {!s.approved && isManager && (
                      <Button size="sm" variant="outline" className="text-emerald-600 hover:text-emerald-700 border-emerald-200" onClick={() => approveSurvey(s.id)}>Freigeben</Button>
                    )}
                    {s.approved && !s.closed && !mine && <Button data-testid={`survey-vote-${s.id}`} size="sm" onClick={() => { setVoteFor(s); setVoteChoice(null); setVoteComment(""); }}>Abstimmen</Button>}
                    {s.approved && mine && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Abgestimmt</Badge>}
                    {isManager && !s.closed && <Button size="icon" variant="ghost" onClick={() => closeSurvey(s.id)}><Lock className="w-4 h-4" /></Button>}
                    {isManager && <Button size="icon" variant="ghost" onClick={() => del(s.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-12">Keine Umfragen</div>}
      </div>

      {/* Create dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Neue Umfrage</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Frage / Titel" data-testid="survey-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea placeholder="Beschreibung (optional)" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div>
              <Label className="text-sm">Antwortoptionen</Label>
              <div className="space-y-2 mt-1">
                {form.options.map((o, i) => (
                  <div key={i} className="flex gap-2">
                    <Input data-testid={`survey-option-${i}`} placeholder={`Option ${i + 1}`} value={o.label} onChange={(e) => { const opts = [...form.options]; opts[i] = { label: e.target.value }; setForm({ ...form, options: opts }); }} />
                    {form.options.length > 2 && <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, options: form.options.filter((_, x) => x !== i) })}><X className="w-4 h-4" /></Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setForm({ ...form, options: [...form.options, { label: "" }] })}><Plus className="w-3 h-3 mr-1" /> Option</Button>
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Zielgruppe – Positionen (leer = alle)</Label>
              <div className="flex flex-wrap gap-1.5">
                {POSITIONS.map((p) => (
                  <button key={p} type="button" onClick={() => togglePos(p)} className={`text-xs rounded-full px-3 py-1 border ${form.target_positions.includes(p) ? "bg-blue-600 text-white border-blue-600" : "border-border/60 hover:border-blue-400"}`}>{p}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2"><Checkbox checked={form.anonymous} onCheckedChange={(v) => setForm({ ...form, anonymous: !!v })} /><Label>Anonym</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={form.allow_comment} onCheckedChange={(v) => setForm({ ...form, allow_comment: !!v })} /><Label>Kommentar erlaubt</Label></div>
            </div>
            <div><Label>Schließt am (optional)</Label><Input type="date" value={form.closes_at} onChange={(e) => setForm({ ...form, closes_at: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button data-testid="survey-save" onClick={save}>Veröffentlichen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vote dialog */}
      <Dialog open={!!voteFor} onOpenChange={() => setVoteFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{voteFor?.title}</DialogTitle></DialogHeader>
          {voteFor?.description && <p className="text-sm text-slate-500 mb-2">{voteFor.description}</p>}
          <div className="space-y-2">
            {voteFor?.options.map((o) => (
              <label key={o.id} className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer ${voteChoice === o.id ? "border-blue-500 bg-blue-50 dark:bg-blue-950/20" : "border-border/60"}`}>
                <input type="radio" data-testid={`survey-choice-${o.id}`} name="opt" checked={voteChoice === o.id} onChange={() => setVoteChoice(o.id)} />
                <span className="font-medium">{o.label}</span>
              </label>
            ))}
          </div>
          {voteFor?.allow_comment && (
            <Textarea placeholder="Kommentar (optional)" rows={2} value={voteComment} onChange={(e) => setVoteComment(e.target.value)} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoteFor(null)}>Abbrechen</Button>
            <Button data-testid="survey-submit-vote" disabled={!voteChoice} onClick={vote}><Send className="w-4 h-4 mr-2" /> Absenden</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
